import { useMemo } from 'react';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ManualAlarmIds {
  /** Unique trimmed ids parsed from the textarea, including invalid ones. */
  ids: string[];
  /** Valid UUIDs that are not already picked from a situation. */
  toSubmit: string[];
  /** Count of ids that will actually be submitted. */
  count: number;
  /** Ids that don't match the UUID validator. */
  invalidIds: string[];
  hasError: boolean;
  errorText: string;
}

export function useManualAlarmIds(
  text: string,
  pickedSet: Set<string>
): ManualAlarmIds {
  const ids = useMemo(
    () =>
      // Split on whitespace too: pasting newline-separated ids into the
      // single-line input makes browsers join them with spaces.
      Array.from(
        new Set(
          text
            .split(/[\s,;]+/)
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        )
      ),
    [text]
  );

  const invalidIds = useMemo(
    () => ids.filter((id) => !UUID_PATTERN.test(id)),
    [ids]
  );

  const toSubmit = useMemo(
    () => ids.filter((id) => UUID_PATTERN.test(id) && !pickedSet.has(id)),
    [ids, pickedSet]
  );

  const errorText = useMemo(() => {
    if (invalidIds.length === 0) return '';
    const sample = invalidIds.slice(0, 3).join(', ');
    const overflow =
      invalidIds.length > 3 ? ` and ${invalidIds.length - 3} more` : '';
    return `Invalid alarm id${
      invalidIds.length > 1 ? 's' : ''
    }: ${sample}${overflow}`;
  }, [invalidIds]);

  return {
    ids,
    toSubmit,
    count: toSubmit.length,
    invalidIds,
    hasError: invalidIds.length > 0,
    errorText,
  };
}
